import { ChevronRight, Check, Coins } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import type { ProviderGroup } from "./useModelSelector";
import type { MediaType } from "./types";

const PROVIDER_NAME_KEYS: Record<string, "providerOpenAI" | "providerGemini" | "providerKling" | "providerSora" | "providerBFL"> = {
  openai: "providerOpenAI",
  gemini: "providerGemini",
  runware: "providerKling",
  sora: "providerSora",
  bfl: "providerBFL",
};

interface ModelSelectorProviderListProps {
  providerGroups: ProviderGroup[];
  selectedModelId: string;
  expandedProvider: string | null;
  onProviderClick: (provider: string) => void;
  onModelSelect: (modelId: string) => void;
  mediaType: MediaType;
  getModelCost: (modelId: string) => number;
  costsLoading: boolean;
}

function CreditBadge({ cost, label, loading }: { cost: number; label: string; loading: boolean }) {
  if (loading) {
    return <span className="text-[11px] text-muted-foreground">...</span>;
  }
  if (cost < 0) return null;

  return (
    <span className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
      <Coins className="w-3 h-3 text-amber-500" />
      <span className="font-semibold text-foreground/80">{cost}</span>
      <span className="text-muted-foreground/70">{label}</span>
    </span>
  );
}

export function ModelSelectorProviderList({
  providerGroups,
  selectedModelId,
  expandedProvider,
  onProviderClick,
  onModelSelect,
  mediaType,
  getModelCost,
  costsLoading,
}: ModelSelectorProviderListProps) {
  const { t } = useLanguage();

  const costLabel = mediaType === "image" ? t.generate.perImage : t.generate.perVideo;

  return (
    <div className="flex flex-col gap-0.5">
      {providerGroups.map((group) => {
        const isSingleModel = group.models.length === 1;
        const isExpanded = expandedProvider === group.provider;
        const selectedModel = group.models.find((m) => m.id === selectedModelId);
        const isGroupSelected = !!selectedModel;
        const providerNameKey = PROVIDER_NAME_KEYS[group.provider];
        const providerName = providerNameKey
          ? t.generate[providerNameKey]
          : `[Missing provider: ${group.provider}]`;

        const providerIcon = group.models[0]?.providerIcon;

        return (
          <div key={group.provider}>
            <button
              onClick={() => {
                if (isSingleModel) {
                  onModelSelect(group.models[0].id);
                } else {
                  onProviderClick(group.provider);
                }
              }}
              className={cn(
                "flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-start transition-colors",
                isGroupSelected
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-accent text-foreground"
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                {providerIcon && (
                  <img
                    src={providerIcon}
                    alt=""
                    className={cn(
                      "w-4 h-4 shrink-0",
                      (group.provider === "openai" || group.provider === "bfl") && "invert dark:invert-0"
                    )}
                  />
                )}
                <span className="text-sm font-medium truncate">
                  {providerName}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isSingleModel && group.models[0].badge === "recommended" && (
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-400/90 text-amber-950 text-[10px] font-bold leading-none">
                    {t.generate.recommended}
                  </span>
                )}
                {isSingleModel && group.models[0].badge === "new" && (
                  <span className="px-1.5 py-0.5 rounded-full bg-blue-500/90 text-white text-[10px] font-bold leading-none">
                    {t.generate.new}
                  </span>
                )}
                {isSingleModel ? (
                  <CreditBadge
                    cost={getModelCost(group.models[0].id)}
                    label={costLabel}
                    loading={costsLoading}
                  />
                ) : (
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 text-muted-foreground transition-transform",
                      isExpanded && "rotate-90"
                    )}
                  />
                )}
              </div>
            </button>

            {/* Expanded model sub-list for multi-model providers */}
            {!isSingleModel && isExpanded && (
              <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-2">
                {group.models.map((model) => {
                  const isSelected = model.id === selectedModelId;
                  return (
                    <button
                      key={model.id}
                      onClick={() => onModelSelect(model.id)}
                      className={cn(
                        "flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-start transition-colors",
                        isSelected
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-accent text-foreground"
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">
                          {model.displayName}
                        </span>
                        {model.badge === "recommended" && (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-400/90 text-amber-950 text-[10px] font-bold leading-none shrink-0">
                            {t.generate.recommended}
                          </span>
                        )}
                        {model.badge === "new" && (
                          <span className="px-1.5 py-0.5 rounded-full bg-blue-500/90 text-white text-[10px] font-bold leading-none shrink-0">
                            {t.generate.new}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <CreditBadge
                          cost={getModelCost(model.id)}
                          label={costLabel}
                          loading={costsLoading}
                        />
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
